import { useLayoutEffect } from 'react'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/browser'
import { selectAppUser } from 'modules/App/store/selectors'

const GUEST_USER_ID_KEY = 'sentry_guest_user_id'

let initialized = false

const hasAuthTokens = () => Boolean(localStorage.getItem('refresh_token'))

const getOrCreateGuestUserId = () => {
  try {
    let id = localStorage.getItem(GUEST_USER_ID_KEY)

    if (!id) {
      id =
        typeof crypto?.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      localStorage.setItem(GUEST_USER_ID_KEY, id)
    }

    return id
  } catch {
    return 'guest-unknown'
  }
}

const setGuestUser = () => {
  Sentry.setUser({ id: getOrCreateGuestUserId() })
}

const setAuthenticatedUser = (user) => {
  Sentry.setUser({
    id: String(user.id),
    ...(user.email ? { email: user.email } : {}),
  })
}

const syncUser = (user) => {
  if (!Sentry.isInitialized()) {
    return
  }

  if (user?.id) {
    setAuthenticatedUser(user)
    return
  }

  if (hasAuthTokens()) {
    return
  }

  setGuestUser()
}

const initSentry = () => {
  if (initialized || process.env.NODE_ENV !== 'production') {
    return
  }

  initialized = true

  const sentryRelease =
    process.env.REACT_APP_SENTRY_RELEASE ||
    (typeof global !== 'undefined' && global.SENTRY_RELEASE && global.SENTRY_RELEASE.id) ||
    undefined

  const likelyAuthenticated = hasAuthTokens()

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN || '',
    tunnel: '/communication',
    ...(sentryRelease ? { release: sentryRelease } : {}),
    ...(!likelyAuthenticated
      ? {
          initialScope: {
            user: { id: getOrCreateGuestUserId() },
          },
        }
      : {}),
    integrations: (defaultIntegrations) => [
      ...defaultIntegrations.filter((integration) => integration.name !== 'GlobalHandlers'),
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: false,
      }),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  })

  console.logXHR = () => {}
}

const SentryManager = () => {
  const user = useSelector(selectAppUser)

  useLayoutEffect(() => {
    initSentry()
    syncUser(user)
  }, [user?.id, user?.email])

  return null
}

export default SentryManager
